/**
 * Textos e valores exibidos na escolha de plano pós-onboarding.
 * Checkout e vínculo com entidade Plan virão depois.
 *
 * Preços base em centavos (BRL) para equivalente mensal e comparação com o mensal.
 */

/** Mensalidade de referência do plano mensal (R$ 79,90). */
export const POST_ONBOARDING_DISPLAY_MONTHLY_CENTS = 7990;

/** Funcionalidades incluídas na assinatura (vitrine de planos). */
export const POST_ONBOARDING_PLAN_FEATURES = [
  'Cadastro de clientes e serviços com histórico e acompanhamento em um só lugar.',
  'Envio de notificações pelo WhatsApp para o seu número: relatórios mensais, lembretes de manutenção e demais avisos que você configurar no fluxo.',
  'Mesmo sistema em todos os planos — muda só o período de cobrança (mensal, semestral ou anual).',
] as const;

/** Widget único de garantia (abaixo do grid de planos). */
export const CHOOSE_PLAN_GUARANTEE_TITLE = '7 dias de garantia em todos os planos';
export const CHOOSE_PLAN_GUARANTEE_BODY =
  'Se não fizer sentido para você, reembolsamos o valor pago no período da garantia. Sem burocracia — vale para mensal, semestral e anual.';

export type PostOnboardingPlanOfferId = 'monthly' | 'semiannual' | 'annual';

export interface PostOnboardingPlanOfferDisplay {
  id: PostOnboardingPlanOfferId;
  title: string;
  priceLine: string;
  priceSub: string;
  billingNote: string;
  highlight: boolean;
  highlightLabel?: string;
  /**
   * Total cobrado no checkout do período (centavos BRL), alinhado a {@link priceLine}.
   */
  checkoutTotalCents: number;
  /**
   * Valor médio por mês no período (total ÷ meses), em centavos.
   * Só semestral/anual — para comparar com {@link POST_ONBOARDING_DISPLAY_MONTHLY_CENTS}.
   */
  equivalentMonthlyCents?: number;
  /**
   * Quanto se economiza no período vs. pagar a mensalidade em cada um dos meses (N × mensal − total do período).
   */
  savingsTotalCents?: number;
}

/** Converte centavos BRL em valor decimal (ex.: amount em reais). */
export function planCheckoutAmountBrlFromCents(totalCents: number): number {
  return totalCents / 100;
}

export function getPostOnboardingPlanOfferById(
  id: PostOnboardingPlanOfferId | null | undefined
): PostOnboardingPlanOfferDisplay | undefined {
  if (!id) return undefined;
  return POST_ONBOARDING_PLAN_OFFERS.find((o) => o.id === id);
}

/** Centavos por mês quando o valor do período é diluído (arredondado ao centavo mais próximo). */
export function computeEquivalentMonthlyCents(
  totalPeriodCents: number,
  monthsInPeriod: number
): number {
  if (monthsInPeriod <= 0) return 0;
  return Math.round(totalPeriodCents / monthsInPeriod);
}

/**
 * Economia total vs. multiplicar a mensalidade de referência pelo número de meses do período.
 * `monthlyBaselineCents` costuma ser o preço do plano mensal no Firestore.
 */
export function computeSavingsVsMonthlyStackedCents(
  totalPeriodCents: number,
  monthsInPeriod: number,
  monthlyBaselineCents: number = POST_ONBOARDING_DISPLAY_MONTHLY_CENTS
): number {
  const baseline = monthlyBaselineCents * monthsInPeriod;
  return Math.max(0, baseline - totalPeriodCents);
}

export const POST_ONBOARDING_PLAN_OFFERS: readonly PostOnboardingPlanOfferDisplay[] = [
  {
    id: 'monthly',
    title: 'Mensal',
    priceLine: 'R$ 79,90',
    priceSub: '/mês',
    billingNote: 'Cobrança todo mês',
    highlight: false,
    checkoutTotalCents: POST_ONBOARDING_DISPLAY_MONTHLY_CENTS,
  },
  {
    id: 'semiannual',
    title: 'Semestral',
    priceLine: 'R$ 399,50',
    priceSub: '/semestre',
    billingNote: 'Cobrança a cada 6 meses',
    highlight: true,
    highlightLabel: 'Mais adquirido',
    checkoutTotalCents: 39950,
    equivalentMonthlyCents: computeEquivalentMonthlyCents(39950, 6),
    savingsTotalCents: computeSavingsVsMonthlyStackedCents(39950, 6),
  },
  {
    id: 'annual',
    title: 'Anual',
    priceLine: 'R$ 749,90',
    priceSub: '/ano',
    billingNote: 'Cobrança uma vez por ano',
    highlight: false,
    highlightLabel: 'Melhor economia',
    checkoutTotalCents: 74990,
    equivalentMonthlyCents: computeEquivalentMonthlyCents(74990, 12),
    savingsTotalCents: computeSavingsVsMonthlyStackedCents(74990, 12),
  },
];
